/**
 * Content Reactions Service
 *
 * Domain layer for content reactions (likes). Datasource-agnostic: all
 * Firestore access is delegated to firestoreService.
 *
 * Mirrors Flutter's ContentReactionsRepository.getContentLikedUsers
 * (lib/data/repository/content_reactions_repository.dart:131-135), which
 * forwards to the data source and returns the paginated like response. Here
 * the raw `likes` page is remapped into lightweight grid-row entities via the
 * existing UserMediaLikeRemapper, with pagination state forwarded unchanged.
 */

import type { PageCursor } from '@/data/types/json';
import { firestoreService } from '@/data/services/firestoreService';
import { UserMediaLikeRemapper } from '@/domain/remappers/userMediaLike/userMediaLikeRemapper';
import type { ContentLikedUserEntity } from '@/domain/entities/contentReaction/contentLikedUserEntity';

export interface ContentLikedUsersPage {
  likedUsers: ContentLikedUserEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class ContentReactionsService {
  private static instance: ContentReactionsService;

  public static getInstance(): ContentReactionsService {
    if (!ContentReactionsService.instance) {
      ContentReactionsService.instance = new ContentReactionsService();
    }
    return ContentReactionsService.instance;
  }

  /**
   * Paginated fetch of users who liked a given content. Remaps each raw
   * `likes` document into a ContentLikedUserEntity via the existing
   * UserMediaLikeRemapper and forwards the pagination state (hasMore, cursor)
   * computed by the data layer. The `usersBlockedMe` filter is applied later
   * in the use case, matching Flutter's data-source/repository split.
   */
  async fetchContentLikedUsers(
    mediaPath: string,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<ContentLikedUsersPage> {
    const page = await firestoreService.getContentLikedUsers(mediaPath, limitCount, cursor);
    return {
      likedUsers: page.items.map(item =>
        UserMediaLikeRemapper.toEntity(UserMediaLikeRemapper.fromJson(item)),
      ),
      hasMore: page.hasMore,
      cursor: page.cursor,
    };
  }
}

export const contentReactionsService = ContentReactionsService.getInstance();
